import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import PersonCell from '../../components/ui/PersonCell';

// Şiddet renkleri — backend'in ShiftComplianceRules ürettiği error/warning/info
// değerleriyle birebir eşlenir. "off" yalnız ayar tarafında var; motor "off"
// şiddetinde ihlal üretmediği için burada yer almaz.
const SEVERITY_COLORS = {
  error: '#E53935',
  warning: '#F59E0B',
  info: '#0291EF',
};

const formatDateDisplay = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
};

// ─── Styled Components (Generate/Clear drawer deseni — ShiftSchedulesPage.js) ─

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #E8ECF0;
`;

const DrawerTitle = styled.h3`
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: none;
  color: ${({ theme }) => theme.colors.text.dark?.little || '#A0A8B4'};
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.bgColor}; }
`;

const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

// ─── Violation list ─────────────────────────────────────────────

const PersonGroup = styled.div`
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid #F0F2F5;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const PersonName = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 8px;
`;

const DateGroup = styled.div`
  margin-bottom: 10px;
  padding-left: 4px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DateLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #7A8694;
  margin-bottom: 4px;
`;

const ViolationRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 4px 0;
`;

const SeverityDot = styled.span`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  margin-top: 5px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

const ViolationMessage = styled.span`
  flex: 1;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.5;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  color: #7A8694;
  font-size: 14px;
`;

const Empty = styled.div`
  padding: 24px;
  text-align: center;
  color: #7A8694;
  font-size: 14px;
`;

export default function ComplianceDrawer({ isOpen, onClose, violations, loading }) {
  const { t } = useTranslation();

  const renderMessage = (v) => {
    const opts = {};
    (v.messageArgs || []).forEach((a, i) => { opts[i] = a; });
    return t(v.messageKey, opts);
  };

  // Kişi → tarih gruplu görünüm. Backend düz liste döner (Task 10); FE burada gruplar.
  const groups = useMemo(() => {
    const byUser = new Map();
    (violations || []).forEach((v) => {
      if (!byUser.has(v.userId)) {
        byUser.set(v.userId, {
          userId: v.userId,
          userFullName: v.userFullName,
          userProfilePicture: v.userProfilePicture,
          byDate: new Map(),
        });
      }
      const { byDate } = byUser.get(v.userId);
      if (!byDate.has(v.date)) byDate.set(v.date, []);
      byDate.get(v.date).push(v);
    });
    return Array.from(byUser.values()).map((u) => ({
      userId: u.userId,
      userFullName: u.userFullName,
      userProfilePicture: u.userProfilePicture,
      dates: Array.from(u.byDate.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([date, items]) => ({ date, items })),
    }));
  }, [violations]);

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      direction="right"
      size="45%"
      duration={150}
      overlayOpacity={0.2}
      style={{
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100vw',
        minWidth: 380,
        margin: '12px',
        borderRadius: '6px',
        height: 'calc(100vh - 24px)',
        boxShadow: '0 8px 32px rgba(0, 66, 66, 0.12)',
      }}
    >
      <DrawerHeader>
        <DrawerTitle>{t('shiftSchedule.checkCompliance')}</DrawerTitle>
        <CloseBtn onClick={onClose} aria-label={t('common.close')}>
          <X size={20} />
        </CloseBtn>
      </DrawerHeader>

      <DrawerBody>
        {loading && (
          <LoadingState>{t('shiftSchedule.loading')}</LoadingState>
        )}

        {!loading && groups.length === 0 && (
          <Empty>{t('shiftCompliance.noViolations')}</Empty>
        )}

        {!loading && groups.map((group) => (
          <PersonGroup key={group.userId}>
            <PersonName>
              <PersonCell name={group.userFullName} profilePicture={group.userProfilePicture} size={24} />
            </PersonName>
            {group.dates.map((dateGroup) => (
              <DateGroup key={dateGroup.date}>
                <DateLabel>{formatDateDisplay(dateGroup.date)}</DateLabel>
                {dateGroup.items.map((v, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <ViolationRow key={`${v.ruleCode}-${i}`}>
                    <SeverityDot $color={SEVERITY_COLORS[v.severity] || SEVERITY_COLORS.info} />
                    <ViolationMessage>{renderMessage(v)}</ViolationMessage>
                  </ViolationRow>
                ))}
              </DateGroup>
            ))}
          </PersonGroup>
        ))}
      </DrawerBody>
    </Drawer>
  );
}
